import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';

import { Heading } from '@zillow/constellation';

import { RMW_PATH } from 'app/shared/pathsManifest';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import ClaimActions from 'app/shared/actions/ClaimActions';
import ModalFooter from 'app/shared/components/Modal/ModalFooter';
import ChooseMethod from './components/ChooseMethod';
import ContactMethodLookup from './components/ContactMethodLookup';
import SearchResults from './components/SearchResults';

import { StyledModalDialog } from './StyledComponents';

const { addContactMethod, propertyLookup } = ClaimActions;
const { track } = analyticsUtils;
const {
    CLAIM_LISTINGS_LABEL: {
        PROPERTY_LOOKUP_MODAL,
        LISTING_RESULTS_MODAL,
        PHONE_LOOKUP_MODAL,
        EMAIL_LOOKUP_MODAL,
    },
    GA_EVENTS: {
        CLAIM_LISTINGS_CLICK,
        CLAIM_LISTINGS_CLOSE_LOOKUP_MODAL,
    },
} = AnalyticsConstants;

const propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onContactMethodAddedSuccess: PropTypes.func.isRequired,
};

const defaultProps = {
    isOpen: true,
};

const STEPS = {
    INIT: 'init',
    CONTACT_METHOD_LOOKUP: 'contactMethodLookup',
    RESULTS: 'results',
};

const PropertyLookupModal = ({
    isOpen,
    onClose,
    onContactMethodAddedSuccess,
}) => {
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [currentStep, setCurrentStep] = useState(STEPS.INIT);
    const [contactValue, setContactValue] = useState('');
    const [contactMethod, setContactMethod] = useState('');
    const [listings, setListings] = useState([]);

    const MODAL_HEADING_MAPPING = {
        [STEPS.INIT]: 'Choose a property lookup method',
        [STEPS.CONTACT_METHOD_LOOKUP]: `${capitalize(contactMethod)} Lookup`,
        [STEPS.RESULTS]: 'Listing Search',
    };

    const ANALYTICS_LABEL_MAPPING = {
        [STEPS.INIT]: PROPERTY_LOOKUP_MODAL,
        [STEPS.CONTACT_METHOD_LOOKUP]: contactMethod === 'phone' ? PHONE_LOOKUP_MODAL : EMAIL_LOOKUP_MODAL,
        [STEPS.RESULTS]: LISTING_RESULTS_MODAL,
    };

    const handleSearchListings = () => dispatch(propertyLookup(contactMethod, contactValue))
        .then((newListings) => {
            setListings(newListings);
            setCurrentStep(STEPS.RESULTS);
        });

    const handleChooseAnotherContactMethod = () => {
        setListings([]);
        setContactMethod('');
        setContactValue('');
        setCurrentStep(STEPS.INIT);
    };

    const handleContactMethodLookup = (contactType) => {
        dispatch(track.GAEventV2(CLAIM_LISTINGS_CLICK,
            {
                label: PROPERTY_LOOKUP_MODAL,
                action: `Begins${capitalize(contactType)}Lookup`,
            }));
        setContactMethod(contactType);
        setCurrentStep(STEPS.CONTACT_METHOD_LOOKUP);
    };

    const handleHelpClick = () => window.open(RMW_PATH.LISTING_CLAIMING_HELP, '_blank');

    const handleAddContactMethodClick = () => {
        dispatch(track.GAEventV2(CLAIM_LISTINGS_CLICK,
            {
                label: LISTING_RESULTS_MODAL,
                action: 'AddsListings',
                value: listings.length,
            }));
        setIsSubmitting(true);
        dispatch(addContactMethod(contactMethod, contactValue))
            .then(() => {
                onClose();
                onContactMethodAddedSuccess();
            })
            .catch(() => setHasError(true))
            .finally(() => setIsSubmitting(false));
    };

    const handleOnClose = () => {
        dispatch(track.GAEventV2(CLAIM_LISTINGS_CLOSE_LOOKUP_MODAL,
            { label: ANALYTICS_LABEL_MAPPING[currentStep] }));
        onClose();
    };

    const handleCancelAddContactMethod = () => {
        dispatch(track.GAEventV2(CLAIM_LISTINGS_CLICK,
            {
                label: LISTING_RESULTS_MODAL,
                action: 'CancelLookupAssociation',
                value: listings.length,
            }));
        onClose();
    };

    const renderContent = () => {
        if (currentStep === STEPS.INIT) {
            return (
                <ChooseMethod
                    onContactMethodLookup={handleContactMethodLookup}
                    onHelpClick={handleHelpClick}
                />
            );
        }
        if (currentStep === STEPS.CONTACT_METHOD_LOOKUP) {
            return (
                <ContactMethodLookup
                    contactMethod={contactMethod}
                    contactValue={contactValue}
                    onChooseAnotherContactMethod={handleChooseAnotherContactMethod}
                    onSearchListings={handleSearchListings}
                    setContactValue={setContactValue}
                />
            );
        }
        if (currentStep === STEPS.RESULTS) {
            return (
                <SearchResults
                    contactMethod={contactMethod}
                    contactValue={contactValue}
                    listings={listings}
                    onAddContactMethod={handleAddContactMethodClick}
                    onChooseAnotherContactMethod={handleChooseAnotherContactMethod}
                    onHelpClick={handleHelpClick}
                />
            );
        }
        return null;
    };

    const renderFooter = () => {
        if (currentStep === STEPS.RESULTS && listings.length > 0) {
            return (
                <ModalFooter
                    hasError={hasError}
                    isSubmitting={isSubmitting}
                    cancelText="Cancel"
                    submitText="Add listings"
                    onCancel={handleCancelAddContactMethod}
                    onSubmit={handleAddContactMethodClick}
                />
            );
        }
        return null;
    };

    return (
        <StyledModalDialog
            body={renderContent()}
            footer={renderFooter()}
            header={<Heading level={6}>{MODAL_HEADING_MAPPING[currentStep]}</Heading>}
            isOpen={isOpen}
            mediaQuery={{ xs: { fullScreen: true }, md: { fullScreen: false } }}
            onClose={handleOnClose}
            wide={true}
        />
    );
};

PropertyLookupModal.propTypes = propTypes;
PropertyLookupModal.defaultProps = defaultProps;

export default PropertyLookupModal;
